import {useObvioUser} from 'obvio/auth'
import Page from 'organization/Event/TemplateEditor/Page'
import React from 'react'
import {FeaturesBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import {TemplateSponsors} from 'Event/Sponsors'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useTemplate} from 'Event/TemplateProvider'
import {DropResult} from 'react-beautiful-dnd'
import {orderedIdsByPosition, createPositions} from 'lib/list'
import {NavButtonProps} from 'Event/Dashboard/components/NavButton'
import ToggleSponsorsSwitch from 'organization/Event/SponsorsConfig/ToggleSponsorsSwitch'
import {generateHashId} from 'lib/crypto/hash'
import {
  useAddTranslation,
  useRemoveTranslations,
  useTranslationLanguage,
} from 'Event/LanguageProvider'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import LanguageSelector from 'organization/Event/LocalizationConfig/LanguageSelector'

export default function SponsorsConfig() {
  const user = useObvioUser()

  return (
    <FeaturesBreadcrumbs page="Sponsors">
      <Page
        toolbarItems={
          <>
            <LanguageSelector />
            <ToggleSponsorsSwitch />
          </>
        }
      >
        <TemplateSponsors user={user} />
      </Page>
    </FeaturesBreadcrumbs>
  )
}

export function useHandleDragSponsors() {
  const update = useSaveTemplate()
  const {
    sponsors: {items},
  } = useTemplate()

  return (result: DropResult) => {
    const {destination, source} = result
    if (!destination) {
      return
    }
    const ids = orderedIdsByPosition(items)

    const [removed] = ids.splice(source.index, 1)
    ids.splice(destination.index, 0, removed)

    update({
      sponsors: {
        items: createPositions(ids),
      },
    })
  }
}

export function useButtons(sponsorId: string) {
  const template = useTemplate()
  const {items} = template.sponsors

  const buttons = items[sponsorId].buttons || {}
  const updateTemplate = useSaveTemplate()
  const addTranslation = useAddTranslation()
  const removeTranslation = useRemoveTranslations()
  const targetLanguage = useTranslationLanguage()

  const add = (button: NavButtonProps) => {
    generateHashId([
      'sponsor_buttons',
      sponsorId,
      Date.now().toString(),
      Math.random().toString(),
    ]).then((id) => {
      updateSponsorButtons(`sponsor_button_${id}`, button)
    })
  }

  const duplicate = (id: string) => {
    generateHashId([
      'sponsor_buttons',
      sponsorId,
      Date.now().toString(),
      Math.random().toString(),
    ]).then((hash) => {
      const newId = `sponsor_button_${hash}`
      const sourceText =
        template.localization?.translations?.[targetLanguage]?.[
          `sponsor_button_${id}_text`
        ] ?? ''

      updateTemplate({
        sponsors: {
          items: {
            [sponsorId]: {
              buttons: {
                [newId]: {
                  ...buttons[id],
                  text: `{{sponsor_button_${newId}_text}}`,
                },
              },
            },
          },
        },
        localization: addTranslation({
          [`sponsor_button_${newId}_text`]: sourceText,
        }),
      })
    })
  }

  const remove = (id: string) => {
    updateTemplate({
      sponsors: {
        items: {
          [sponsorId]: {
            buttons: {
              [id]: REMOVE,
            },
          },
        },
      },
      localization: removeTranslation([`sponsor_button_${id}_text`]),
    })
  }

  const update = (id: string) => (button: NavButtonProps) => {
    updateSponsorButtons(id, button)
  }

  const handleDrag = (result: DropResult) => {
    const {destination, source} = result

    if (!destination) {
      return
    }

    const ids = orderedIdsByPosition(buttons)
    const [removed] = ids.splice(source.index, 1)
    ids.splice(destination.index, 0, removed)

    const positions = createPositions(ids)

    updateTemplate({
      sponsors: {
        items: {
          [sponsorId]: {
            buttons: positions,
          },
        },
      },
    })
  }

  const updateSponsorButtons = (id: string, updated: NavButtonProps) => {
    const text = replaceAtPath(updated, 'text', `{{sponsor_button_${id}_text}}`)

    updateTemplate({
      sponsors: {
        items: {
          [sponsorId]: {
            buttons: {
              [id]: updated,
            },
          },
        },
      },
      localization: addTranslation({
        [`sponsor_button_${id}_text`]: text ?? '',
      }),
    })
  }

  return {
    add,
    remove,
    update,
    duplicate,
    handleDrag,
  }
}
